import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';

import {
    PENDING,
    VALIDATED,
    CANCELED,
    INSUFFICIENT_FOUND,
    WAITING_TRANSACTION
} from '../../../../services/checkout/constants';
import { PATH } from '../../../../constants/path';
import { KYC_USE_CASE_WAITING_TRANSACTION } from '../../../../services/kyc/constants';
import { MODAL_TYPE_KYC, MODAL_TYPE_LOGIN } from '../../../../services/modal/constants';

import { showModal } from '../../../../services/modal/actions';
import { fetchPaypalDetails } from '../../../../services/paypal/actions';
import { fetchCheckoutDetails } from '../../../../services/checkout/actions';
import { fetchEbonOrderByIdWithCode } from '../../../../services/ebon/actions';
import { fetchUserInfo } from '../../../../services/account/myprofile/actions';
import { logoutUser } from '../../../../services/authentication/logout/actions';
import { fetchUserBalance } from '../../../../services/account/balance/actions';

import { isTokenExpired } from '../../../../services/authentication/utils';

const ConfirmationEbonPageStateController = ({
    orderId,
    partnerId,
    partnerName,
    checkoutSessionId,
    checkoutPaymentId,
    paypalCost,
    paymentRef,
    setLoading,
    setKyc,
    kycValidated,
    ebonOrderState,
    setEbonOrderState,
    detailsInterval,
    sessionEmail,
    history,
    fetchingOrder,
    fetchingCheckoutDetails,
    checkoutDetails,
    confirmedOrderState,
    fetchingPaypalDetails,
    paypalDetails,
    token,
    isAuthenticated,
    fetchEbonOrderByIdWithCode,
    fetchCheckoutDetails,
    fetchPaypalDetails,
    showModal,
    logoutUser,
    fetchUserBalance
}) => {
    const [pendingInterval, setPendingInterval] = useState(null);

    const isPaypalPayment = !!paypalCost;
    // Checkout par défaut : Apple Pay / Google Pay reviennent parfois sans cko-*
    const isCheckoutPayment = !isPaypalPayment && !!orderId;

    const fetchDetails = () => {
        if (isCheckoutPayment) {
            fetchCheckoutDetails(orderId, checkoutPaymentId);
        } else if (isPaypalPayment) {
            fetchPaypalDetails(paymentRef);
        }
    };

    /* Authentication check */
    useEffect(() => {
        checkAuthentication();
    });

    const isMemberAuthenticated = () => !isTokenExpired(token) && isAuthenticated;

    const checkAuthentication = () => {
        if (!isMemberAuthenticated()) {
            new Promise((resolve, reject) => {
                history.push(PATH.HOME_PATH);
                resolve();
            }).then(() => logoutUser(() => showModal(MODAL_TYPE_LOGIN)));
        }
    };

    /* Handle loading */
    useEffect(() => {
        const fetchingPaymentDetails =
            (isCheckoutPayment && fetchingCheckoutDetails) ||
            (isPaypalPayment && fetchingPaypalDetails);

        // sans état PSP, l'état de la commande suffit
        const orderAlreadyValidated = confirmedOrderState === VALIDATED;

        const waitingForEbonState =
            ((isCheckoutPayment || isPaypalPayment) && !ebonOrderState && !orderAlreadyValidated) ||
            ebonOrderState === PENDING;

        setLoading(Boolean(fetchingOrder || fetchingPaymentDetails || waitingForEbonState));
    }, [
        fetchingOrder,
        fetchingCheckoutDetails,
        fetchingPaypalDetails,
        ebonOrderState,
        confirmedOrderState
    ]);

    /* Fetch order on display */
    useEffect(() => {
        if (isMemberAuthenticated()) {
            fetchEbonOrderByIdWithCode(orderId);
            fetchUserInfo();
        }
    }, []);

    /* Fetch order state on display */
    useEffect(() => {
        setTimeout(() => {
            fetchDetails();
        }, 1000);
    }, [checkoutSessionId, paypalCost]);

    // Delay balance fetching because balance update is not instantaneous on server side
    useEffect(() => {
        setTimeout(() => {
            fetchUserBalance();
        }, 1000);
    }, []);

    /* Set order state */
    useEffect(() => {
        if (isPaypalPayment) {
            return setEbonOrderState(paypalDetails);
        } else {
            return setEbonOrderState(checkoutDetails);
        }
    }, [checkoutDetails, paypalDetails]);

    /* Handle order states */
    useEffect(() => {
        if (!ebonOrderState || ebonOrderState === PENDING) {
            if (!pendingInterval) {
                setPendingInterval(
                    setInterval(() => {
                        fetchDetails();
                    }, 5000)
                );
                return () => {
                    clearInterval(pendingInterval);
                };
            }
        }

        if (!kycValidated && ebonOrderState && ebonOrderState === WAITING_TRANSACTION) {
            clearInterval(pendingInterval);
            clearInterval(detailsInterval);
            setKyc(true);
            showModal(MODAL_TYPE_KYC, {
                hideCloseButton: true,
                useCase: KYC_USE_CASE_WAITING_TRANSACTION
            });
        }

        if (ebonOrderState === CANCELED || ebonOrderState === INSUFFICIENT_FOUND) {
            clearInterval(pendingInterval);
            clearInterval(detailsInterval);
            history.push(`${PATH.EBON_ERROR_PATH}-${partnerId}?paiement=error&orderId=${orderId}`);
        }

        if (ebonOrderState === VALIDATED) {
            clearInterval(pendingInterval);
            clearInterval(detailsInterval);
        }
    }, [ebonOrderState]);

    return null;
};

ConfirmationEbonPageStateController.propTypes = {
    orderId: PropTypes.string,
    partnerName: PropTypes.string,
    partnerId: PropTypes.number,
    checkoutSessionId: PropTypes.string,
    checkoutPaymentId: PropTypes.string,
    paypalCost: PropTypes.string,
    paymentRef: PropTypes.string,
    setLoading: PropTypes.func,
    setKyc: PropTypes.func,
    kycValidated: PropTypes.bool,
    ebonOrderState: PropTypes.string,
    confirmedOrderState: PropTypes.string,
    setEbonOrderState: PropTypes.func,
    detailsInterval: PropTypes.number,
    sessionEmail: PropTypes.string
};

const mapStateToProps = state => {
    return {
        fetchingOrder: state.ebon.fetchOrder,
        fetchingCheckoutDetails: state.checkout.fetchingDetails,
        checkoutDetails: state.checkout.details ? state.checkout.details.ebonOrderState : null,
        confirmedOrderState: state.ebon.confirmedOrder ? state.ebon.confirmedOrder.state : null,
        fetchingPaypalDetails: state.paypal.fetchingDetails,
        paypalDetails: state.paypal.details ? state.paypal.details.ebonOrderState : null,
        token: state.authentication.token,
        isAuthenticated: state.authentication.isAuthenticated
    };
};

const mapDispatchToProps = {
    fetchEbonOrderByIdWithCode,
    fetchCheckoutDetails,
    fetchPaypalDetails,
    showModal,
    logoutUser,
    fetchUserBalance
};

export default withRouter(
    connect(mapStateToProps, mapDispatchToProps)(ConfirmationEbonPageStateController)
);



// WEBPACK FOOTER //
// ./src/components/pages/ebon/confirmation/ConfirmationEbonPageStateController.js